import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { FileText, Search, ExternalLink, BookOpen, Filter, Eye } from 'lucide-react';
import DocumentViewer from '../components/DocumentViewer';

const DocumentLibrary = () => {
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchResults, setSearchResults] = useState(null);
  const [viewingDocId, setViewingDocId] = useState(null);

  const fetchDocuments = async () => {
    const data = await apiFetch(`${API}/documents/registry`);
    if (data?._error) {
      console.error('Error fetching documents:', data.message);
      setLoading(false);
      return;
    }
    setDocuments(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    const params = new URLSearchParams({ query: searchQuery });
    if (selectedCategory !== 'all') {
      params.set('category', selectedCategory);
    }
    const data = await apiFetch(`${API}/documents/search?${params}`);
    if (data?._error) {
      console.error('Error searching documents:', data.message);
      return;
    }
    setSearchResults(data);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const categories = documents ? Object.keys(documents.categories) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg p-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={36} />
          <div>
            <h1 className="text-3xl font-bold">Regulatory Document Library</h1>
            <p className="text-indigo-100">EU & International ESG Regulations</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="text-sm text-slate-600">Total Documents</div>
          <div className="text-3xl font-bold text-slate-900">{documents?.total_documents || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="text-sm text-slate-600">Categories</div>
          <div className="text-3xl font-bold text-slate-900">{categories.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="text-sm text-slate-600">Last Updated</div>
          <div className="text-lg font-semibold text-slate-900">
            {new Date(documents?.last_updated).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Search Documents</h2>
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by keywords (e.g., climate change, CSRD, scope 3...)"
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button
            onClick={handleSearch}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
        </div>

        {/* Search Results */}
        {searchResults && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Search Results ({searchResults.results_count})
            </h3>
            {searchResults.results.length === 0 ? (
              <p className="text-slate-600">No documents found matching your query.</p>
            ) : (
              <div className="space-y-3">
                {searchResults.results.map((doc, idx) => (
                  <DocumentCard key={idx} doc={doc} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Documents by Category */}
      <div className="space-y-6">
        {documents && Object.entries(documents.categories).map(([category, docs]) => (
          <div key={category} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-100 px-6 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">{category}</h2>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold">
                  {docs.length} document{docs.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {docs.map((doc, idx) => (
                <DocumentCard key={idx} doc={doc} onView={setViewingDocId} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Document Viewer Modal */}
      {viewingDocId && (
        <DocumentViewer
          docId={viewingDocId}
          onClose={() => setViewingDocId(null)}
        />
      )}
    </div>
  );
};

const DocumentCard = ({ doc, onView }) => {
  const hasEmbeddedContent = ['ESRS_1', 'ESRS_2', 'ESRS_E1', 'CSRD', 'EU_TAXONOMY'].includes(doc.doc_id);
  
  return (
    <div className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="text-indigo-600 flex-shrink-0" size={20} />
            <div>
              <h3 className="font-semibold text-slate-900">{doc.name}</h3>
              <p className="text-sm text-slate-600">{doc.code}</p>
            </div>
            {hasEmbeddedContent && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                TEXT AVAILABLE
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mb-3">{doc.summary}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {doc.key_topics.slice(0, 5).map((topic, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs"
              >
                {topic}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>📍 {doc.jurisdiction}</span>
            <span>📅 {doc.year}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {hasEmbeddedContent && (
            <button
              onClick={() => onView(doc.doc_id)}
              className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <Eye size={16} />
              View Document
            </button>
          )}
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            <ExternalLink size={16} />
            Official PDF
          </a>
        </div>
      </div>
    </div>
  );
};

export default DocumentLibrary;
